import { useQuery } from "@tanstack/react-query";
import { useState, useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import Container from "../../componentes/Container";
import { BotaoComTextoEIcone } from "@digix-ui/componentes";
import { CampoDeTexto } from "../../componentes";
import { buscarLocaisAtendimento } from "./services/buscarLocaisAtendimento";
import { buscarCidades } from "./services/buscarCidades";
import "./styles.scss";
import Paginacao from "../GerenciarPermissoesLocaisAtendimento/components/Paginacao";

interface LocalAtendimento {
  id: number;
  nome: string;
  cidade: string;
  uf: string;
  tipo: string;
}

type Filtros = {
  nome: string;
  cidade: string | number;
};

const ListagemDeLocaisAtendimento = () => {
  const { control, handleSubmit } = useForm<Filtros>({
    defaultValues: {
      nome: "",
      cidade: "",
    },
  });

  const [paginaAtual, setPaginaAtual] = useState(1);
  const [filtrosAplicados, setFiltrosAplicados] = useState<Filtros>({
    nome: "",
    cidade: "",
  });
  const [menuAbertoIndex, setMenuAbertoIndex] = useState<number | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const { data: dataCidades, isLoading: loadingCidades } = useQuery({
    queryKey: ["cidades"],
    queryFn: buscarCidades,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ["locais", filtrosAplicados, paginaAtual],
    queryFn: () =>
      buscarLocaisAtendimento({
        descricao: filtrosAplicados.nome,
        idCidade: filtrosAplicados.cidade,
        paginaAtual,
      }),
  });

  const locais: LocalAtendimento[] =
    data?.modelo.map((item) => {
      const [cidade, uf] = item.cidadeComUf.split("/");
      return {
        id: item.id,
        nome: item.descricao,
        cidade,
        uf,
        tipo: item.descricao,
      };
    }) ?? [];

  const abrirMenu = (index: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setMenuAbertoIndex((atual) => (atual === index ? null : index));
  };

  useEffect(() => {
    const fecharMenu = () => setMenuAbertoIndex(null);
    window.addEventListener("click", fecharMenu);
    return () => window.removeEventListener("click", fecharMenu);
  }, []);

  function irParaPagina(pagina: number) {
    if (pagina <= 0) return;
    if (data && pagina > data.totalDePaginas) return;
    setPaginaAtual(pagina);
  }

  const onSubmit = (valores: Filtros) => {
    setFiltrosAplicados(valores);
    setPaginaAtual(1);
  };

  const colunas = ["Local", "UF", "Cidade", "Tipo de local", "Ações"];

  return (
    <Container comFundo tamanhoDoContainer="grande" comCabecalhoFixo>
      <div className="formulario">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "0.5rem",
            width: "100%",
          }}
        >
          <h2 className="cartao__titulo u-texto-negrito" style={{ margin: 0 }}>
            Locais de atendimento
          </h2>
          <div style={{ marginLeft: "auto" }}>
            <BotaoComTextoEIcone
              tipo="button"
              cor="sucesso"
              tamanho="pequeno"
              icone="fas fa-plus"
              posicaoDoIcone="esquerda"
              onClick={() =>
                (window.location.href = "/cadastrar-local-atendimento")
              }
            >
              Cadastrar novo local de atendimento
            </BotaoComTextoEIcone>
          </div>
        </div>

        <p className="u-texto-pequeno u-margem-top-grande">
          Gerencie e cadastre os locais de atendimento. Defina as permissões de
          acesso para cada local, garantindo que apenas os usuários autorizados
          possam atender em cada unidade.
        </p>

        <form
          className="grade__linha u-margem-superior-media"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Controller
            name="nome"
            control={control}
            render={({ field }) => (
              <CampoDeTexto
                {...field}
                label="Filtrar por nome do local"
                placeholder="Digite o nome"
                colunas="4"
              />
            )}
          />

          <Controller
            name="cidade"
            control={control}
            render={({ field }) => (
              <div
                className="campo"
                style={{ gridColumn: "span 4", marginTop: "0.45rem" }}
              >
                <label className="campo__rotulo">Filtrar por cidade</label>
                <select
                  {...field}
                  className="campo__entrada"
                  disabled={loadingCidades}
                >
                  <option value="">Todas</option>
                  {dataCidades?.map((cidade) => (
                    <option key={cidade.id} value={cidade.id}>
                      {cidade.nome}
                    </option>
                  ))}
                </select>
              </div>
            )}
          />

          <div
            className="u-align-self-end"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              marginTop: "1.5rem",
            }}
          >
            <BotaoComTextoEIcone
              tipo="submit"
              cor="secundaria"
              icone="fas fa-search"
              tamanho="pequeno"
              posicaoDoIcone="esquerda"
            >
              Pesquisar
            </BotaoComTextoEIcone>
          </div>
        </form>

        <table className="tabela tabela_com-largura-total tabela_listrada tabela_com-borda u-margem-superior-media">
          <thead>
            <tr>
              {colunas.map((coluna, idx) => (
                <th
                  key={coluna}
                  className={`is-cor-cinza-20 u-texto-alinhado-ao-${
                    idx === 0 ? "esquerda" : "centro"
                  }`}
                >
                  {coluna}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              [...Array(5)].map((_, i) => (
                <tr key={i}>
                  {colunas.map((_, j) => (
                    <td key={j}>
                      <div className="skeleton skeleton-linha" />
                    </td>
                  ))}
                </tr>
              ))
            ) : error ? (
              <tr>
                <td
                  colSpan={colunas.length}
                  className="u-texto-alinhado-ao-centro u-cor-vermelha"
                >
                  Ocorreu um erro ao carregar os locais de atendimento.
                </td>
              </tr>
            ) : locais.length === 0 ? (
              <tr>
                <td
                  colSpan={colunas.length}
                  className="u-texto-alinhado-ao-centro"
                >
                  Nenhum local encontrado.
                </td>
              </tr>
            ) : (
              locais.map((local, index) => (
                <tr key={local.id}>
                  <td>{local.nome}</td>
                  <td className="u-texto-alinhado-ao-centro">{local.uf}</td>
                  <td className="u-texto-alinhado-ao-centro">{local.cidade}</td>
                  <td className="u-texto-alinhado-ao-centro">{local.tipo}</td>
                  <td className="u-texto-alinhado-ao-centro">
                    <div className="acoes" ref={menuRef}>
                      <BotaoComTextoEIcone
                        tamanho="pequeno"
                        cor="terciaria"
                        tipo="button"
                        posicaoDoIcone="direita"
                        visual="sem-preenchimento"
                        icone="far fa-chevron-down"
                        onClick={() => {
                          const e = window.event as MouseEvent;
                          e?.stopPropagation();
                          abrirMenu(index);
                        }}
                      >
                        Ações
                      </BotaoComTextoEIcone>
                      {menuAbertoIndex === index && (
                        <ul className="inscricao-acoes__lista">
                          <li className="inscricao-acoes__item">
                            <button
                              className="inscricao-acoes__botao"
                              onClick={() =>
                                (window.location.href = `/gerenciar-locais/editar/${local.id}`)
                              }
                            >
                              <i className="inscricao-acoes__icone far fa-map-marker-alt"></i>{" "}
                              Editar local
                            </button>
                          </li>
                          <li className="inscricao-acoes__item">
                            <button
                              className="inscricao-acoes__botao"
                              onClick={() =>
                                (window.location.href = `/gerenciar-permissoes-locais-de-atendimento/${local.id}`)
                              }
                            >
                              <i className="inscricao-acoes__icone far fa-folder"></i>{" "}
                              Gerenciar permissões
                            </button>
                          </li>
                        </ul>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {data && (
          <>
            <div className="u-margem-top-media u-texto-pequeno">
              {data.totalDeRegistros} resultado(s)
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "1rem",
              }}
            >
              <Paginacao
                paginaAtual={paginaAtual}
                totalDePaginas={data.totalDePaginas}
                onPageChange={irParaPagina}
              />
            </div>
          </>
        )}
      </div>
    </Container>
  );
};

export default ListagemDeLocaisAtendimento;
